/**
 * KioskCheckOut — visitor check-out flow (ADR-073 Decision 5).
 *
 * Visitors can check out by entering their badge number or name.
 */
import { useEffect, useState } from 'react';
import {
  Box,
  Center,
  Field,
  Heading,
  HStack,
  Input,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { usePageTitle } from '../../hooks/usePageTitle';
import { Button as ButtonComponent, CardButton } from '@themes/aegix';
import { useNavigate } from 'react-router-dom';
import {
  useCheckOut,
  useActiveVisits,
} from '../../hooks/queries/useVisitorManagement';
import { useKiosk } from '../../contexts/KioskContext';
import { toaster } from '../../components/ui/toaster';
import { HiArrowLeft, HiCheckCircle } from 'react-icons/hi2';
import type { VisitListItem } from '../../types/VisitorManagementTypes';

export function KioskCheckOut() {
  const { t } = useTranslation();
  usePageTitle('Kiosk');
  const navigate = useNavigate();
  const { schoolId } = useKiosk();
  const { data: activeVisits, isLoading } = useActiveVisits(
    schoolId ?? undefined,
  );
  const checkOutMutation = useCheckOut();

  const [searchQuery, setSearchQuery] = useState('');
  const [checkedOut, setCheckedOut] = useState(false);
  const [checkoutCountdown, setCheckoutCountdown] = useState(10);
  const [checkoutPaused, setCheckoutPaused] = useState(false);

  // Filter active visits by badge number or name
  const filteredVisits =
    searchQuery.length >= 2
      ? (activeVisits ?? []).filter((v: VisitListItem) => {
          const query = searchQuery.toLowerCase();
          return (
            v.badge_number.toLowerCase().includes(query) ||
            v.visitor_name.toLowerCase().includes(query)
          );
        })
      : [];

  const handleCheckOut = (visit: VisitListItem) => {
    checkOutMutation.mutate(
      { visit_public_id: visit.public_id },
      {
        onSuccess: () => {
          setCheckedOut(true);
        },
        onError: (err) => {
          toaster.create({
            title: t('kiosk.checkOutFailed', 'Check-out failed'),
            description: err.message,
            type: 'error',
          });
        },
      },
    );
  };

  // Returns the kiosk to the welcome screen after check-out. Load-bearing on
  // an unattended lobby device: without it the confirmation screen — which
  // names the visitor — stays up until someone taps "Return now", so the next
  // person in line sees the previous visitor's identity. Pauses at 3s so a
  // visitor who is still reading can stop the countdown.
  useEffect(() => {
    if (!checkedOut) return;
    if (checkoutPaused || checkoutCountdown <= 0) {
      if (checkoutCountdown <= 0) navigate('/kiosk', { replace: true });
      return;
    }
    if (checkoutCountdown === 3) {
      setCheckoutPaused(true);
      return;
    }
    const timer = setTimeout(() => setCheckoutCountdown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [checkedOut, checkoutCountdown, checkoutPaused, navigate]);

  if (checkedOut) {
    return (
      <Box minH="100vh" bg="bg.subtle" py={12} px={4}>
        <Center maxW="500px" mx="auto">
          <VStack gap={6} align="center" textAlign="center">
            <Box color="fg.success">
              <HiCheckCircle size={72} />
            </Box>
            <Heading as="h1" size="xl">
              {t('kiosk.checkedOut', 'Checked Out')}
            </Heading>
            <Text color="text.secondary">
              {t('kiosk.thankYouForVisiting', 'Thank you for visiting!')}
            </Text>
            {checkoutPaused ? (
              <Box
                role="alert"
                aria-live="assertive"
                bg="yellow.50"
                _dark={{ bg: 'yellow.900' }}
                border="1px solid"
                borderColor="yellow.500"
                borderRadius="md"
                p={4}
                textAlign="center"
              >
                <Text fontWeight="600" mb={3}>
                  {t(
                    'kiosk.returnWelcomeNotice',
                    'This screen will return to the welcome page.',
                  )}
                </Text>
                <HStack gap={3} justify="center">
                  <ButtonComponent
                    visual="primary"
                    onClick={() => {
                      setCheckoutCountdown(10);
                      setCheckoutPaused(false);
                    }}
                    minH="44px"
                    data-e2e="kiosk-stay-on-page-button"
                  >
                    {t('kiosk.stayOnThisPage', 'Stay on this page')}
                  </ButtonComponent>
                  <ButtonComponent
                    visual="secondary"
                    onClick={() => navigate('/kiosk', { replace: true })}
                    minH="44px"
                    data-e2e="return-now-button"
                  >
                    {t('kiosk.returnNow', 'Return now')}
                  </ButtonComponent>
                </HStack>
              </Box>
            ) : (
              <Text fontSize="xs" color="text.secondary" aria-live="polite">
                {t(
                  'kiosk.returningToWelcomeCountdown',
                  'Returning to welcome screen in {{seconds}}s',
                  { seconds: checkoutCountdown },
                )}
              </Text>
            )}
          </VStack>
        </Center>
      </Box>
    );
  }

  return (
    <Box minH="100vh" bg="bg.subtle" py={8} px={4}>
      <Box maxW="600px" mx="auto">
        <ButtonComponent
          visual="secondary"
          onClick={() => navigate('/kiosk')}
          mb={6}
          size="sm"
          minH="44px"
          minW="44px"
          data-e2e="back-button"
        >
          <HiArrowLeft size={16} />
          <Text ml={2}>{t('common.back', 'Back')}</Text>
        </ButtonComponent>

        <Heading as="h1" size="lg" mb={6} textAlign="center">
          {t('kiosk.visitorCheckOut', 'Visitor Check-Out')}
        </Heading>

        <VStack gap={5} align="stretch">
          <Field.Root>
            <Field.Label>
              {t('kiosk.badgeNumberOrName', 'Badge Number or Name')}
            </Field.Label>
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t(
                'kiosk.enterBadgeOrNamePlaceholder',
                'Enter your badge number or name',
              )}
              size="lg"
              // eslint-disable-next-line jsx-a11y/no-autofocus -- kiosk check-out: unattended walkup flow, focus the badge search input
              autoFocus
            />
          </Field.Root>

          {isLoading && (
            <Center py={4}>
              <Spinner size="md" />
            </Center>
          )}

          {filteredVisits.length > 0 && (
            <VStack gap={2} align="stretch">
              <Text fontSize="sm" fontWeight="600">
                Select your visit:
              </Text>
              {filteredVisits.map((v: VisitListItem) => (
                <CardButton
                  key={v.public_id}
                  size="lg"
                  onClick={() => handleCheckOut(v)}
                  aria-label={`Check out ${v.visitor_name}, badge ${v.badge_number}`}
                  description={`Badge: ${v.badge_number} | Checked in: ${new Date(
                    v.checked_in_at,
                  ).toLocaleTimeString()}`}
                >
                  {v.visitor_name}
                </CardButton>
              ))}
            </VStack>
          )}

          {searchQuery.length >= 2 &&
            filteredVisits.length === 0 &&
            !isLoading && (
              <Text color="text.secondary" textAlign="center" py={4}>
                No active visits found matching &ldquo;{searchQuery}&rdquo;
              </Text>
            )}
        </VStack>
      </Box>
    </Box>
  );
}
